import React, { useState } from 'react';
import { makeStyles, useTheme } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import CardCheckbox from 'Components/UI/CardCheckbox';
import BulkCommunicationAddHousehold from 'Components/Modals/BulkCommunicationDialog/components/BulkCommunicationAddHousehold';
import Paper from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Paper';
import useResource from 'Hooks/useResource';
import Typography from 'Components/UI/Typography';
import Button from 'Components/UI/Button';
import { addQuery, removeQuery } from 'Helpers/Query';
import BulkCommunicationFilters from 'Components/Modals/BulkCommunicationDialog/components/BulkCommunicationFilters/BulkCommunicationFilters';
import RecommendedMessageCard from 'Components/Modals/BulkCommunicationDialog/components/MessageCard';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import IconButton from '@material-ui/core/IconButton';
import fr from 'facet-rest';
import useUserContext from 'Context/UserContext';
import classNames from 'classnames';
import MetaField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import useMessagesStatsContext from 'Context/MessagesStatsContext';
import AdditionalBulkCommunicationRecipients
  from 'Components/Modals/BulkCommunicationDialog/components/AdditionalBulkCommunicationRecipients';

const useStyles = makeStyles((theme) => ({
  root: {
    // display: 'flex',
  },
  row: {
    display: 'flex',
  },
  textField: {
    width: '100%',
  },
  group: {
    width: 200,
  },
  groupContainer: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(3),
  },
  addHousehold: {
    width: 'fit-content',
    marginBottom: theme.spacing(2),
  },
  addHouseholdContainer: {
    flexGrow: 1,
  },
  paper: {
    padding: theme.spacing(2),
  },
  card: {
    width: 275,
  },
  cardContainer: {
    display: 'inline-flex',
    gap: `${theme.spacing(1)}px`,
    flexWrap: 'wrap',
    justifyContent: 'center',
    padding: theme.spacing(0, 4),
  },
  headerRow: {
    display: 'flex',
    padding: theme.spacing(2, 4),
  },
  divider: {
    borderLeft: `1px solid ${theme.palette.facet.mediumGray}`,
    margin: `0 ${theme.spacing(1)}px`,
    height: '2em',
  },
  selectToggle: {
    cursor: 'pointer',
  },
  text: {
    padding: theme.spacing(0.75),
  },
  filterGroupsContainer: {
    display: 'flex',
    marginBottom: theme.spacing(2),
  },
  nextMeetingFilter: {
    padding: 0,
  },
  marginBelow: {
    marginBottom: theme.spacing(1.5),
  },
  mainContent: {
  },
  content: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    marginBottom: theme.spacing(4),
    alignItems: 'center',
  },
  backButton: {
    height: '48px',
    width: '48px',
    marginRight: theme.spacing(2),
    color: theme.palette.facet.accentBlue,
  },
  backButtonIcon: {
    height: '48px',
    width: '48px',
  },
  additionalRecipientsContainer: {
    padding: theme.spacing(0, 4),
    width: '100%',
  },
  additionalRecipients: {
    width: '100%',
  },
  hidden: {
    display: 'none',
  },
}));

const BulkCommunicationLayout = () => {
  const classes = useStyles();
  const theme = useTheme();
  const { resource: resourceFromContext } = useMetaLayoutContext();
  const { resource, setAttribute } = useResource(resourceFromContext);
  const { handleMessagesStatsRefresh } = useMessagesStatsContext();

  const { agentResource } = useUserContext();

  const isEveryFilteredRecipientSelected = resource?.attr('filteredRecipients')?.resources().list().every(el => el.attr('isIncluded') === 'true');

  const addManualRecipient = (v) => {
    if (v && !resource.attr('addedRecipients')?.find(existing => existing === v.id())) {
      v.attr('isIncluded', 'true');
      resource.attr('addedRecipients').push(v.id());
      setAttribute('addedRecipients', resource.attr('addedRecipients'));
    }
  };

  const removeManualRecipient = (v) => {
    const index = resource.attr('addedRecipients').indexOf(v);
    const addedRecipientFound = index > -1;
    if (addedRecipientFound) {
      resource.attr('addedRecipients').splice(index, 1);
    }
    setAttribute('addedRecipients', resource.attr('addedRecipients'));
  };

  const handleClickSelectAllToggle = () => {
    if (!resource) return;
    const val = isEveryFilteredRecipientSelected ? 'false' : 'true';
    resource.attr('filteredRecipients').resources().list().forEach(el => {
      el.attr('isIncluded', val);
    });
    setAttribute('filteredRecipients', resource.attr('filteredRecipients').copy());
  };

  const onSetAdditionalRecipient = (additionalRecipients) => {
    if (!resource) return;
    setAttribute('additionalRecipients', additionalRecipients);
  };

  const selectedRecipientCount = (
    resource.attr('filteredRecipients')?.resources().list().filter(el => el.attr('isIncluded') === 'true').length || 0)
      + (resource.attr('addedRecipients')?.length || 0);

  const recipientTotal = (
    resource.attr('filteredRecipients')?.resources().length() || 0)
      + (resource.attr('addedRecipients')?.length || 0);

  const [filterString, setFilterString] = useState(resource.attr('filters'));

  const handleApplyFilters = (filterStr) => {
    setFilterString(filterStr);
  };

  const canAddRecipients = !!resource.meta().field('addedRecipients');

  const bulkCommIsUnsentDraft = () => resource.attr('isDraft') === 'true' && resource.attr('status') !== 'complete';

  return (
    <div className={classes.root} test="bulk-communication-layout">
      {resource.link('self') && (
        <div className={classNames(
          classes.header,
          classes.row,
        )}
        >
          <IconButton
            className={classes.backButton}
            test={'bulk-communications-back-button'}
            size={'large'}
            onClick={(evt) => {
              evt.stopPropagation();
              addQuery(
                { modal: 'bulkCommunication' },
                { bulkCommunicationLink: agentResource?.relationship(fr.relationships.bulkCommunication.name).link('related').url() },
              );
            }}
          >
            <ChevronLeftIcon className={classes.backButtonIcon} />
          </IconButton>
          {!bulkCommIsUnsentDraft()
            ? <RecommendedMessageCard
              title={resource.attr('title')}
              sendAt={resource.attr('sendAt')}
            /> : null}
        </div>
      )}
      <div className={classes.mainContent}>
        <div className={classes.row}>
          <div className={classes.content}>
            <MetaField
              attr="subject"
            />
            <MetaField
              attr="content"
              CustomProps={{
                resource,
                setSubject: (subject) => {
                  setAttribute('subject', subject);
                },
                setTemplateId: (templateId => {
                  setAttribute('templateId', templateId);
                }),
                className: classes.textField,
                RichTextEditorProps: { test: 'bulk-communication-message-input', size: 'default', agentOnly: true },
                beforeSubmit: (res) => {
                  res.attr('filters', filterString);
                },
                onSubmit: () => {
                  removeQuery('modal', 'bulkCommunicationLink');
                  handleMessagesStatsRefresh();
                },
              }}
            />
          </div>
        </div>
        <BulkCommunicationFilters
          displayNeedsPlanningMeetingFilter
          resource={resource}
          onApplyFilters={handleApplyFilters}
        />
        <Paper className={classes.paper} elevation={1}>
          {canAddRecipients && (
            <div className={classes.headerRow}>
              <div className={classes.addHouseholdContainer}>
                <BulkCommunicationAddHousehold
                  className={classes.addHousehold}
                  onChange={(val) => {
                    if (val) {
                      addManualRecipient(val);
                    }
                  }}
                  resource={resource}
                />
              </div>
              <Typography
                color={'primary'}
                variant="h6"
                className={classes.text}
              >{`${recipientTotal}`}
              </Typography>
              <span className={classes.divider} />
              <span>
                <Typography
                  color={'primary'}
                  variant="h6"
                  className={classes.text}
                >{`${selectedRecipientCount} Selected`}
                </Typography>
              </span>
              <span className={classes.divider} />
              <span>
                <Button className={classes.selectToggle} onClick={handleClickSelectAllToggle} test="bulk-communication-select-all">
                  <Typography
                    color={'primary'}
                    variant="h6"
                  >
                    {isEveryFilteredRecipientSelected ? 'Unselect All' : 'Select All'}
                  </Typography>
                </Button>
              </span>
            </div>
          )}
          <div className={classes.additionalRecipientsContainer}>
            <AdditionalBulkCommunicationRecipients
              className={classes.additionalRecipients}
              bulkCommunicationResource={resource}
              setAdditionalRecipients={onSetAdditionalRecipient}
            />
          </div>
          <div className={classes.cardContainer}>
            {resource.attr('addedRecipients')?.length > 0 && resource.attr('addedRecipients')?.map((recipientId) => {
              return (
                <CardCheckbox
                  key={`added-recipient-${recipientId}`}
                  className={classes.card}
                  label={resource.meta().field('addedRecipients').options().list()
                    .find(option => option.attr('id') === recipientId)
                    .attr('prompt')}
                  value={'true'}
                  selectedColor={theme.palette.facet.accentGreen}
                  onChange={(event) => {
                    if (event.target.checked) {
                      addManualRecipient(recipientId);
                    } else {
                      removeManualRecipient(recipientId);
                    }
                  }}
                  CheckboxProps={{
                    test: 'added-bulk-communication-recipient',
                  }}
                />
              );
            })}
            {resource.attr('filteredRecipients')?.resources().list().map(recipientId => (
              <CardCheckbox
                key={`filtered-recipient-${recipientId.id()}`}
                className={classes.card}
                label={recipientId.attr('name')}
                value={recipientId.attr('isIncluded') === 'true'}
                onChange={(event) => {
                  recipientId.attr('isIncluded', event.target.checked ? 'true' : 'false');
                  setAttribute('filteredRecipients', resource.attr('filteredRecipients'));
                }}
                CheckboxProps={{
                  test: 'bulk-communication-recipient',
                }}
              />
            ))}
          </div>
          <div className={classes.hidden}><MetaField attr={'additionalRecipients'} resource={resource} /></div>
        </Paper>
      </div>
    </div>
  );
};

export default BulkCommunicationLayout;
